/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade, Pagination } from "swiper/modules";
import { motion } from "framer-motion";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";

import { ArrowRight, Quote } from "lucide-react";
import { HERO_SLIDES, COLLECTIONS, VOICES, BLOG_POSTS } from "../data/content";
import { Reveal, SectionHeader, Stat } from "../components/Shared";
import { API } from "../lib/utils";
import { useContent } from "../lib/content";
import SEO from "../components/SEO";

export default function Home() {
  const [stats, setStats] = useState({
    lives_touched: 12480,
    communities_served: 47,
    stories_documented: 1280,
    scholarships_awarded: 86,
  });
  const [heroSlides, setHeroSlides] = useState(HERO_SLIDES);
  const [collections, setCollections] = useState(COLLECTIONS);
  const [testimonials, setTestimonials] = useState([]);
  const missionOverline = useContent("home.mission.overline", "Our Mission");
  const missionTitle = useContent("home.mission.title", "Healing the world through African art, culture & humanity.");
  const missionBody = useContent("home.mission.body", "We are a photography studio, a foundation, and a movement. Every image we make returns to the lives it captured — as food, school fees, clean water, and dignity.");
  const heroSub = useContent("home.hero.subhead", "Documenting Africa through photography, heritage, storytelling, and humanitarian impact.");
  const heroLine1 = useContent("home.hero.headline_pre", "Stories.");
  const heroLine2 = useContent("home.hero.headline_mid", "Culture.");
  const heroLine3 = useContent("home.hero.headline_post", "Humanity.");
  const featuredImage = useContent("home.featured.image", "");

  // Experience Ada — 6 fully editable cards
  const adaOverline = useContent("home.ada.overline", "Experience Ada · Ghana");
  const adaTitlePre = useContent("home.ada.title_pre", "Where the Volta breathes its last");
  const adaTitleMid = useContent("home.ada.title_accent", "breath into the Atlantic");
  const adaKicker = useContent("home.ada.kicker", "Step into the festivals, food, fishing villages, and sacred grounds I have spent my life photographing. Not as a tourist. As a guest.");
  const adaCtaLabel = useContent("home.ada.cta_label", "Experience Ada");
  const adaCtaLink = useContent("home.ada.cta_link", "/heritage");

  const ada1Title = useContent("home.ada.card1_title", "Asafotufiami Festival");
  const ada1Blurb = useContent("home.ada.card1_blurb", "The annual warrior festival. Drums older than the colonial map. Chiefs in regal cloth. The pulse of the Ada people.");
  const ada1Tag = useContent("home.ada.card1_tag", "Festival");
  const ada1Image = useContent("home.ada.card1_image", "https://static.prod-images.emergentagent.com/jobs/044129a6-116c-4bc2-ad36-aa46b4cd58c6/images/262d2a119ea065c60d4574f67dd6758acb405be3e4d42c7dedd851228252a681.jpeg");

  const ada2Title = useContent("home.ada.card2_title", "Fishing Culture");
  const ada2Blurb = useContent("home.ada.card2_blurb", "Before sunrise. Nets mended by lamplight. The men sing as they push the boat — because singing is how the sea is asked permission.");
  const ada2Tag = useContent("home.ada.card2_tag", "Living tradition");
  const ada2Image = useContent("home.ada.card2_image", "https://static.prod-images.emergentagent.com/jobs/044129a6-116c-4bc2-ad36-aa46b4cd58c6/images/c591339fd1b3548a106dd8624c4e8146aa7a238675166360a9e4951617bfac24.jpeg");

  const ada3Title = useContent("home.ada.card3_title", "Food & Hospitality");
  const ada3Blurb = useContent("home.ada.card3_blurb", "Banku, tilapia, palm soup, kpɔkpɔi — meals that taste like memory. Cooked slow. Served wide. Eaten with hands and prayers.");
  const ada3Tag = useContent("home.ada.card3_tag", "Cuisine");
  const ada3Image = useContent("home.ada.card3_image", "https://static.prod-images.emergentagent.com/jobs/044129a6-116c-4bc2-ad36-aa46b4cd58c6/images/01ba5caf069adaad6260d044c68c327d36e59d2398c525ed984b3fd7ae88c9ae.jpeg");

  const ada4Title = useContent("home.ada.card4_title", "Spiritual Traditions");
  const ada4Blurb = useContent("home.ada.card4_blurb", "Libations at dawn. Cowries on a small altar. Ancestor names whispered before any major decision. The unseen scaffolding of Ada life.");
  const ada4Tag = useContent("home.ada.card4_tag", "Sacred");
  const ada4Image = useContent("home.ada.card4_image", "https://static.prod-images.emergentagent.com/jobs/044129a6-116c-4bc2-ad36-aa46b4cd58c6/images/9a71607853e657533e7260a260278b3d5486c4131f940732b9b328ea8986d4b3.jpeg");

  const ada5Title = useContent("home.ada.card5_title", "Hidden Gems");
  const ada5Blurb = useContent("home.ada.card5_blurb", "The mangrove islands. The salt-lakes. The pottery women of Vume. The places no guidebook lists and locals only show those they trust.");
  const ada5Tag = useContent("home.ada.card5_tag", "Hidden");
  const ada5Image = useContent("home.ada.card5_image", "https://static.prod-images.emergentagent.com/jobs/044129a6-116c-4bc2-ad36-aa46b4cd58c6/images/0a40d7684b59bb2917c796195a6a630a9a2be96189c28bff33c792ce25e64a4f.jpeg");

  const ada6Title = useContent("home.ada.card6_title", "Local Experiences");
  const ada6Blurb = useContent("home.ada.card6_blurb", "Spend a day with a fisherman. A morning with a weaver. An afternoon with the elders. Curated visits that honour the people first.");
  const ada6Tag = useContent("home.ada.card6_tag", "Curated visits");
  const ada6Image = useContent("home.ada.card6_image", "https://static.prod-images.emergentagent.com/jobs/044129a6-116c-4bc2-ad36-aa46b4cd58c6/images/8ffc0c368b47233f0bca468f4647e13f0361dcc0ad7301a94246dff192bc97d7.jpeg");

  const adaCards = [
    { title: ada1Title, blurb: ada1Blurb, image: ada1Image, tag: ada1Tag },
    { title: ada2Title, blurb: ada2Blurb, image: ada2Image, tag: ada2Tag },
    { title: ada3Title, blurb: ada3Blurb, image: ada3Image, tag: ada3Tag },
    { title: ada4Title, blurb: ada4Blurb, image: ada4Image, tag: ada4Tag },
    { title: ada5Title, blurb: ada5Blurb, image: ada5Image, tag: ada5Tag },
    { title: ada6Title, blurb: ada6Blurb, image: ada6Image, tag: ada6Tag },
  ];

  // Final CTA wall — 5 fully editable cards
  const ctaOverline = useContent("home.cta.overline", "Join the Journey");
  const ctaTitlePre = useContent("home.cta.title_pre", "Five ways to walk this");
  const ctaTitleMid = useContent("home.cta.title_accent", "road with me");
  const ctaKicker = useContent("home.cta.kicker", "Whether you support the foundation, commission a body of work, host a talk, or visit Ghana — the door is open. The mission is built on people who choose to walk in.");
  const cta1Label = useContent("home.cta.card1_label", "Support the Mission");
  const cta1Blurb = useContent("home.cta.card1_blurb", "Fund families, schools, water, dignity.");
  const cta1Link = useContent("home.cta.card1_link", "/eyes-of-hope#donate");
  const cta2Label = useContent("home.cta.card2_label", "Work With Me");
  const cta2Blurb = useContent("home.cta.card2_blurb", "Commissions, brand work, partnerships.");
  const cta2Link = useContent("home.cta.card2_link", "/adanobi");
  const cta3Label = useContent("home.cta.card3_label", "Book Storytelling");
  const cta3Blurb = useContent("home.cta.card3_blurb", "Talks, media, panels, interviews.");
  const cta3Link = useContent("home.cta.card3_link", "/contact");
  const cta4Label = useContent("home.cta.card4_label", "Visit Ghana With Us");
  const cta4Blurb = useContent("home.cta.card4_blurb", "Curated cultural journeys to Ada.");
  const cta4Link = useContent("home.cta.card4_link", "/heritage");
  const cta5Label = useContent("home.cta.card5_label", "Join the Journey");
  const cta5Blurb = useContent("home.cta.card5_blurb", "Letters, essays, behind-the-frame.");
  const cta5Link = useContent("home.cta.card5_link", "/journal");

  const ctaCards = [
    { to: cta1Link, label: cta1Label, blurb: cta1Blurb, testId: "final-cta-support" },
    { to: cta2Link, label: cta2Label, blurb: cta2Blurb, testId: "final-cta-work" },
    { to: cta3Link, label: cta3Label, blurb: cta3Blurb, testId: "final-cta-book" },
    { to: cta4Link, label: cta4Label, blurb: cta4Blurb, testId: "final-cta-visit" },
    { to: cta5Link, label: cta5Label, blurb: cta5Blurb, testId: "final-cta-join" },
  ];

  useEffect(() => {
    axios.get(`${API}/impact-stats`).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/hero-slides`).then((r) => {
      if (r.data?.length) setHeroSlides(r.data);
    }).catch(() => {});
    axios.get(`${API}/collections`).then((r) => {
      if (r.data?.length) setCollections(r.data);
    }).catch(() => {});
    axios.get(`${API}/testimonials`).then((r) => setTestimonials(r.data || [])).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page" className="page-enter">
      <SEO
        title="Photographer, Storyteller, Humanitarian"
        path="/"
        description="Mershack Kabu Aklie (Cowrieking) — Ghanaian photographer, storyteller, and founder of Eyes of Hope Foundation. Documenting humanity, preserving African identity, transforming stories into impact."
      />
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative h-screen min-h-[700px] w-full overflow-hidden bg-[#0a0908]"
      >
        <Swiper
          modules={[Autoplay, EffectFade, Pagination]}
          effect="fade"
          autoplay={{ delay: 6000, disableOnInteraction: false }}
          loop
          pagination={{ clickable: true }}
          className="w-full h-full"
        >
          {heroSlides.map((s, i) => (
              <SwiperSlide key={i}>
                <div className="relative w-full h-full">
                  <img
                    src={s.image}
                    alt={s.caption ? `${s.caption} — ${s.location || ''}` : "Documentary photograph by Mershack Kabu Aklie"}
                    loading={i === 0 ? "eager" : "lazy"}
                    fetchPriority={i === 0 ? "high" : "auto"}
                    className="w-full h-full object-cover animate-slow-zoom"
                  />
                <div className="absolute inset-0 hero-gradient" />
                <div className="absolute inset-0 vignette" />
                <div className="absolute bottom-32 right-10 hidden lg:block">
                  <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
                    {s.location}
                  </p>
                  <p className="font-serif italic text-lg text-[#f4f0ec] mt-2 max-w-xs text-right">
                    "{s.caption}"
                  </p>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="absolute inset-0 z-10 flex items-end pb-32 lg:pb-40">
          <div className="max-w-[1600px] w-full mx-auto px-6 sm:px-10 lg:px-16">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-[11px] font-mono uppercase tracking-[0.35em] text-[#d4af37] mb-8"
            >
              Mershack Kabu Aklie · Cowrieking
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              data-testid="hero-headline"
              className="font-serif text-[clamp(2.75rem,8vw,8rem)] text-[#f4f0ec] font-light leading-[0.95] tracking-tight max-w-6xl"
            >
              {heroLine1}
              <br />
              <span className="italic text-[#d4af37]">{heroLine2}</span>
              <br />
              {heroLine3}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 1 }}
              className="mt-7 sm:mt-8 text-[17px] sm:text-xl lg:text-2xl text-[#c4bdb5] max-w-2xl leading-relaxed font-sans"
            >
              {heroSub}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 1 }}
              className="mt-10 flex flex-wrap gap-3 sm:gap-4"
            >
              <Link
                to={cta1Link || "/gallery"}
                data-testid="hero-cta-stories"
                className="btn-primary"
              >
                {cta1Label}
              </Link>
              <Link
                to={cta2Link || "/eyes-of-hope#donate"}
                data-testid="hero-cta-donate"
                className="btn-secondary"
              >
                {cta2Label}
              </Link>
              <Link
                to={cta3Link || "/museum"}
                data-testid="hero-cta-ada"
                className="btn-secondary"
              >
                {cta3Label}
              </Link>
            </motion.div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-[#a8a29e]">
          <span className="text-[10px] font-mono uppercase tracking-[0.3em]">
            Scroll
          </span>
          <div className="w-px h-12 bg-gradient-to-b from-[#d4af37] to-transparent" />
        </div>
      </section>

      {/* MISSION + FEATURED STORY (above-the-fold emotional anchor) */}
      <section
        data-testid="mission-featured-section"
        className="relative py-24 lg:py-32 bg-[#0a0908]"
      >
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-20">
              <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-5">
                {missionOverline}
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#f4f0ec] font-light leading-tight">
                {missionTitle}
              </h2>
              <p className="mt-6 text-base text-[#c4bdb5] leading-relaxed">
                {missionBody}
              </p>
            </div>
          </Reveal>

          {testimonials[0] && (
            <Reveal delay={0.15}>
              <article
                data-testid="featured-story"
                className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center bg-[#141210] border border-[#d4af37]/30 overflow-hidden"
              >
                <div className="lg:col-span-6 relative aspect-[4/5] lg:aspect-auto lg:h-full min-h-[480px]">
                  {(featuredImage || testimonials[0].image) && (
                    <img
                      src={featuredImage || testimonials[0].image}
                      alt={`Portrait of ${testimonials[0].author}, ${testimonials[0].role}`}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#0a0908]/70 via-transparent to-transparent" />
                </div>
                <div className="lg:col-span-6 p-8 lg:p-14">
                  <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-6">
                    Featured story · Voices of Humanity
                  </p>
                  <Quote size={32} className="text-[#d4af37] mb-5" aria-hidden="true" />
                  <blockquote className="font-serif italic text-2xl sm:text-3xl text-[#f4f0ec] leading-snug">
                    "{testimonials[0].quote}"
                  </blockquote>
                  <figcaption className="mt-6 text-[#c4bdb5]">
                    — <span className="font-serif text-[#f4f0ec]">{testimonials[0].author}</span>
                    {testimonials[0].role && (
                      <span className="block text-[11px] font-mono uppercase tracking-[0.2em] text-[#d4af37] mt-2">
                        {testimonials[0].role}
                      </span>
                    )}
                  </figcaption>
                  <div className="mt-10 flex flex-wrap gap-3">
                    <Link
                      to="/voices"
                      data-testid="featured-read-story"
                      className="btn-primary"
                    >
                      Read the Stories
                    </Link>
                    <Link
                      to="/eyes-of-hope#donate"
                      data-testid="featured-support"
                      className="btn-secondary"
                    >
                      Support the Mission
                    </Link>
                    <Link
                      to="/voices"
                      data-testid="featured-explore-more"
                      className="text-xs font-mono uppercase tracking-[0.25em] text-[#d4af37] hover:text-[#f4f0ec] px-3 py-3 inline-flex items-center gap-2"
                    >
                      Explore more <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          )}
        </div>
      </section>

      {/* MANIFESTO */}
      <section
        data-testid="manifesto-section"
        className="relative py-32 lg:py-48 bg-[#0a0908]"
      >
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 lg:sticky lg:top-32 h-fit">
            <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-5">
              The Manifesto
            </p>
            <p className="font-serif italic text-2xl text-[#a8a29e] leading-relaxed">
              "A photograph is a cowrie. Small enough to hold, large enough to carry a people."
            </p>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 space-y-10">
            <Reveal>
              <p className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#f4f0ec] font-light leading-[1.15]">
                I was born by the estuary in Ada Foah, where the Volta breathes its last breath into the Atlantic. I learned, before I learned anything else, that water remembers.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-lg text-[#a8a29e] leading-relaxed font-sans">
                My work is not photography. My work is remembering — for the elder who cannot read, for the child who has not yet arrived, for the country that has been told its own face does not matter.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-lg text-[#a8a29e] leading-relaxed font-sans">
                Through <strong className="text-[#f4f0ec]">Eyes of Hope Foundation</strong> and <strong className="text-[#f4f0ec]">Adanobi Studios</strong>, photographs do not stay flat. They feed. They school. They dignify. They build.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <Link
                to="/philosophy"
                data-testid="manifesto-cta"
                className="inline-flex items-center gap-3 text-[#d4af37] hover:text-[#f4f0ec] text-xs font-mono uppercase tracking-[0.25em] transition-colors group pt-4 border-t border-[#d4af37]/30"
              >
                Read the philosophy
                <ArrowRight
                  size={16}
                  className="group-hover:translate-x-2 transition-transform"
                />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* IMPACT STATS */}
      <section
        data-testid="impact-stats-section"
        className="relative py-24 lg:py-32 border-y border-white/5"
        style={{
          backgroundImage:
            "url(https://static.prod-images.emergentagent.com/jobs/044129a6-116c-4bc2-ad36-aa46b4cd58c6/images/0968c25e9d8210741d3ca6c8415c22227c63dffdaefe5973e8091f5a399c9c6a.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-[#0a0908]/85" />
        <div className="relative max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-12 text-center">
              Impact, in flesh and number
            </p>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-16">
            <Stat
              testId="stat-lives"
              value={stats.lives_touched.toLocaleString()}
              label="Lives touched"
            />
            <Stat
              testId="stat-communities"
              value={stats.communities_served}
              label="Communities served"
            />
            <Stat
              testId="stat-stories"
              value={stats.stories_documented.toLocaleString()}
              label="Stories documented"
            />
            <Stat
              testId="stat-scholarships"
              value={stats.scholarships_awarded}
              label="Scholarships funded"
            />
          </div>
        </div>
      </section>

      {/* COLLECTIONS PREVIEW */}
      <section
        data-testid="collections-preview"
        className="py-32 lg:py-48 bg-[#0a0908]"
      >
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
            <SectionHeader
              overline="The Photography"
              title="Six collections. One continent of feeling."
              kicker="Each frame an offering. Each collection a chamber of the same long song."
            />
            <Link
              to="/gallery"
              data-testid="view-all-gallery"
              className="btn-secondary self-start lg:self-end"
            >
              View All Collections
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {collections.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.08}>
                <Link
                  to={`/gallery/${c.slug}`}
                  data-testid={`collection-card-${c.slug}`}
                  className="group block img-hover relative overflow-hidden aspect-[3/4]"
                >
                  <img
                    src={c.cover}
                    alt={c.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0908]/95 via-[#0a0908]/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-7">
                    <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-3">
                      {String(i + 1).padStart(2, "0")} · {(c.images?.length || c.count || 0)} photographs
                    </p>
                    <h3 className="font-serif text-3xl text-[#f4f0ec] leading-tight">
                      {c.title}
                    </h3>
                    <p className="mt-2 text-sm text-[#a8a29e] font-sans">
                      {c.subtitle}
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EYES OF HOPE */}
      <section
        data-testid="eoh-feature"
        className="relative py-32 lg:py-48 overflow-hidden"
        style={{
          backgroundImage:
            "url(https://static.prod-images.emergentagent.com/jobs/044129a6-116c-4bc2-ad36-aa46b4cd58c6/images/28dd368d5bd05c877d7b64ffb072b9b68d140c0d67775beb0ccddea6c00c9133.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-[#0a0908]/80" />
        <div className="relative max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-6">
              Eyes of Hope Foundation
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#f4f0ec] font-light leading-tight">
              Healing the world through African art, culture & humanity.
            </h2>
            <p className="mt-8 text-lg text-[#a8a29e] leading-relaxed font-sans max-w-xl">
              Every photograph I sell, every print collected, every story shared funds the children, families, and elders we document. Photography with purpose, in flesh and number.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/eyes-of-hope"
                data-testid="eoh-learn-more"
                className="btn-primary"
              >
                Explore the Foundation
              </Link>
              <Link
                to="/eyes-of-hope#donate"
                data-testid="eoh-donate-home"
                className="btn-secondary"
              >
                Donate
              </Link>
              <Link
                to="/founders-circle"
                data-testid="eoh-founders-home"
                className="btn-secondary"
              >
                Founder's Circle
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid grid-cols-2 gap-6">
              {[
                { n: "100%", l: "Of print sales reinvested" },
                { n: "86", l: "Scholarships funded" },
                { n: "47", l: "Communities served" },
                { n: "12.4k", l: "Lives touched directly" },
              ].map((s) => (
                <div
                  key={s.l}
                  className="bg-[#0a0908]/60 backdrop-blur-md border border-white/10 p-8"
                >
                  <div className="font-serif text-4xl text-[#d4af37] font-light">
                    {s.n}
                  </div>
                  <p className="mt-3 text-xs font-mono uppercase tracking-[0.2em] text-[#a8a29e]">
                    {s.l}
                  </p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* EXPERIENCE ADA — cultural / tourism / heritage discovery */}
      <section
        id="experience-ada"
        data-testid="experience-ada-section"
        className="py-24 sm:py-32 lg:py-44 bg-[#141210] border-y border-white/5"
      >
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14 lg:mb-20">
            <SectionHeader
              overline={adaOverline}
              title={<>{adaTitlePre} <span className="italic text-[#d4af37]">{adaTitleMid}</span>.</>}
              kicker={adaKicker}
            />
            <Link
              to={adaCtaLink || "/heritage"}
              data-testid="experience-ada-cta"
              className="btn-secondary self-start"
            >
              {adaCtaLabel}
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
            {adaCards.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.06}>
                <article
                  data-testid={`ada-card-${i}`}
                  className="group relative overflow-hidden bg-[#0a0908] border border-white/5 hover:border-[#d4af37]/40 transition-colors duration-500"
                >
                  <div className="relative overflow-hidden aspect-[4/3]">
                    <img
                      src={c.image}
                      alt={c.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0908]/85 via-transparent to-transparent" />
                    <span className="absolute top-4 left-4 text-[10px] font-mono uppercase tracking-[0.25em] text-[#d4af37] bg-[#0a0908]/70 backdrop-blur px-3 py-1.5 border border-[#d4af37]/30">
                      {c.tag}
                    </span>
                  </div>
                  <div className="p-6 sm:p-7">
                    <h3 className="font-serif text-[22px] sm:text-2xl text-[#f4f0ec] leading-snug group-hover:text-[#d4af37] transition-colors">
                      {c.title}
                    </h3>
                    <p className="mt-3 text-[15px] text-[#a8a29e] leading-relaxed font-sans">
                      {c.blurb}
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VOICES PREVIEW */}
      <section
        data-testid="voices-preview"
        className="py-32 lg:py-48 bg-[#0a0908]"
      >
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16">
          <SectionHeader
            overline="Voices of Humanity"
            title="The people in my photographs speak for themselves."
            kicker="A photograph alone is not a story. A story belongs to its keeper."
          />
          <div className="mt-16 grid md:grid-cols-3 gap-8">
            {VOICES.map((v, i) => (
              <Reveal key={v.name} delay={i * 0.1}>
                <div className="group cursor-pointer">
                  <div className="relative overflow-hidden aspect-[4/5] mb-6 img-hover">
                    <img
                      src={v.image}
                      alt={v.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0908]/70 to-transparent" />
                    <Quote
                      size={32}
                      className="absolute top-6 left-6 text-[#d4af37]/70"
                    />
                  </div>
                  <p className="font-serif italic text-xl text-[#f4f0ec] leading-snug">
                    "{v.quote}"
                  </p>
                  <p className="mt-4 text-[11px] font-mono uppercase tracking-[0.25em] text-[#d4af37]">
                    {v.name} · {v.location}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-16 text-center">
            <Link
              to="/voices"
              data-testid="voices-view-all"
              className="btn-secondary"
            >
              Hear More Voices
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS / SOCIAL PROOF */}
      {testimonials.length > 0 && (
        <section data-testid="testimonials-section" className="py-32 lg:py-48 bg-[#0a0908] border-y border-white/5">
          <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16">
            <SectionHeader
              overline="Testimonials"
              title="Words from the people who've worked with us."
            />
            <div className="mt-16 grid md:grid-cols-3 gap-8">
              {testimonials.slice(0, 3).map((t, i) => (
                <Reveal key={t.id || i} delay={i * 0.1}>
                  <figure className="bg-[#141210] border border-white/10 p-8 h-full flex flex-col">
                    <Quote size={28} className="text-[#d4af37] mb-5" />
                    <blockquote className="font-serif italic text-xl text-[#f4f0ec] leading-snug flex-1">
                      "{t.quote}"
                    </blockquote>
                    <figcaption className="mt-6 pt-5 border-t border-white/5 flex items-center gap-4">
                      {t.image && (
                        <img src={t.image} alt={t.author} loading="lazy" decoding="async" className="w-12 h-12 rounded-full object-cover" />
                      )}
                      <div>
                        <p className="font-serif text-[#f4f0ec]">{t.author}</p>
                        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#d4af37] mt-1">
                          {t.role}
                        </p>
                      </div>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* JOURNAL PREVIEW */}
      <section
        data-testid="journal-preview"
        className="py-32 lg:py-48 bg-[#141210] border-y border-white/5"
      >
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
            <SectionHeader
              overline="From The Journal"
              title="Field notes & longform essays."
            />
            <Link to="/journal" className="btn-secondary self-start">
              Read All Essays
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            {BLOG_POSTS.slice(0, 3).map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.1}>
                <Link
                  to={`/journal/${p.slug}`}
                  data-testid={`journal-card-${p.slug}`}
                  className="group block"
                >
                  <div className="relative overflow-hidden aspect-[4/3] mb-6 img-hover">
                    <img
                      src={p.cover}
                      alt={p.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.25em] text-[#d4af37] mb-3">
                    <span>{p.category}</span>
                    <span className="text-[#78716c]">·</span>
                    <span className="text-[#a8a29e]">{p.read}</span>
                  </div>
                  <h3 className="font-serif text-2xl text-[#f4f0ec] leading-snug group-hover:text-[#d4af37] transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#a8a29e] font-sans leading-relaxed">
                    {p.excerpt}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CALL-TO-ACTION WALL */}
      <section
        data-testid="final-cta-wall"
        className="relative py-24 sm:py-32 lg:py-44 bg-[#0a0908] overflow-hidden border-t border-white/5"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0.83 0 0 0 0 0.69 0 0 0 0 0.22 0 0 0 0.8 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center top, rgba(212,175,55,0.08) 0%, rgba(10,9,8,0) 60%)",
          }}
        />

        <div className="relative max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
              <div className="flex items-center justify-center mb-7">
                <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#d4af37]" />
                <div className="h-px w-20 bg-[#d4af37]" />
                <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#d4af37]" />
              </div>
              <p className="text-[11px] font-mono uppercase tracking-[0.4em] text-[#d4af37] mb-5">
                {ctaOverline}
              </p>
              <h2 className="font-serif text-[clamp(2.25rem,5vw,4rem)] text-[#f4f0ec] font-light leading-[1.05]">
                {ctaTitlePre} <span className="italic text-[#d4af37]">{ctaTitleMid}</span>.
              </h2>
              <p className="mt-6 text-base sm:text-lg text-[#a8a29e] leading-relaxed font-sans">
                {ctaKicker}
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 lg:gap-6">
            {ctaCards.map((c, i) => (
              <Reveal key={c.testId} delay={i * 0.06}>
                <Link
                  to={c.to}
                  data-testid={c.testId}
                  className="mission-cta group relative block h-full p-6 lg:p-7 bg-[#141210]/60 border border-[#d4af37]/25 hover:border-[#d4af37] transition-all duration-500 overflow-hidden"
                >
                  <span
                    aria-hidden="true"
                    className="mission-cta-shimmer pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 bg-gradient-to-r from-transparent via-[#d4af37]/20 to-transparent"
                  />
                  <div className="relative flex flex-col h-full">
                    <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-4">
                      0{i + 1}
                    </p>
                    <h3 className="font-serif text-[20px] sm:text-[22px] text-[#f4f0ec] group-hover:text-[#d4af37] transition-colors duration-500 leading-snug">
                      {c.label}
                    </h3>
                    <p className="mt-3 text-[14px] text-[#a8a29e] leading-relaxed font-sans">
                      {c.blurb}
                    </p>
                    <span className="mt-6 pt-4 border-t border-white/5 inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-[#d4af37] opacity-70 group-hover:opacity-100 transition-opacity">
                      Continue
                      <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.5}>
            <p className="mt-16 text-center text-[11px] font-mono uppercase tracking-[0.4em] text-[#78716c]">
              Ada · Accra · The world
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
